import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Car, Loader2, Upload, CheckCircle2 } from "lucide-react";

const readAsDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = () => rej(new Error("read failed"));
  r.readAsDataURL(file);
});

const DocInput = ({ label, testid, value, onChange, required }) => {
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_100_000) { toast.error(`${label}: file must be < 1MB`); return; }
    try { onChange(await readAsDataUrl(file)); }
    catch { toast.error(`Failed to read ${label}`); }
  };
  return (
    <div>
      <Label className="text-xs uppercase text-slate-400">{label}{required && <span className="text-red-400 ml-1">*</span>}</Label>
      <label className="mt-2 flex items-center gap-3 h-24 border border-dashed border-white/15 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30 transition-colors px-4">
        <div className={`w-10 h-10 rounded-full grid place-items-center ${value ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-400/15 text-amber-400"}`}>
          {value ? <CheckCircle2 className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
        </div>
        <div className="text-sm">
          <div className="font-medium">{value ? "Uploaded — tap to replace" : "Tap to upload image (JPG/PNG, <1MB)"}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
        <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid} />
      </label>
      {value && (
        <img src={value} alt={label} className="mt-2 h-16 rounded border border-white/10" />
      )}
    </div>
  );
};

export default function DriverApply() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", email: "", phone: "",
    license_number: "", license_expiry: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "",
    vehicle_plate: "", insurance_expiry: "",
    doc_license: null, doc_insurance: null, doc_profile: null, doc_vehicle: null,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.doc_license || !f.doc_insurance || !f.doc_profile) {
      toast.error("Please upload licence, insurance and profile photo");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/driver/apply", {
        ...f, vehicle_year: Number(f.vehicle_year) || 0,
      });
      setDone(data);
      toast.success("Application submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#090D14] grid place-items-center text-white p-4">
        <div className="w-full max-w-lg glass-card rounded-2xl p-10 gold-border text-center" data-testid="apply-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-6">
            <CheckCircle2 className="w-9 h-9 text-emerald-400" />
          </div>
          <h1 className="font-display text-3xl font-extrabold">Application received</h1>
          <p className="text-slate-400 mt-3">Reference <span className="font-mono text-amber-400">{done.reference}</span></p>
          <p className="text-sm text-slate-400 mt-4">Our team will review your documents and email you an approval or update within 1–2 business days.</p>
          <Button onClick={() => nav("/driver/login")} className="mt-8 btn-gold w-full" data-testid="apply-back-to-login">Back to sign-in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090D14] text-white p-4 py-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/driver/login" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-[#090D14] font-black text-xl font-display">T</div>
          <div><div className="font-display font-extrabold text-white text-xl">Triptap Drivers</div><div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 font-mono">Apply to drive</div></div>
        </Link>
        <form onSubmit={submit} className="glass-card rounded-2xl p-8 gold-border space-y-6">
          <div>
            <div className="text-caption mb-2 flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Driver application</div>
            <h1 className="font-display text-2xl font-bold">Join the Triptap fleet</h1>
            <p className="text-sm text-slate-400 mt-1">Complete this form to get on the road. All fields marked * are required.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase text-slate-400">Full name *</Label><Input required value={f.name} onChange={(e) => upd("name", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-name" /></div>
            <div><Label className="text-xs uppercase text-slate-400">Email *</Label><Input required type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-email" /></div>
            <div className="sm:col-span-2"><Label className="text-xs uppercase text-slate-400">Mobile *</Label><Input required value={f.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+64 21 123 4567" className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-phone" /></div>
          </div>

          <div className="border-t border-white/5 pt-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Licence</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase text-slate-400">Licence number *</Label><Input required value={f.license_number} onChange={(e) => upd("license_number", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-license" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Expiry date *</Label><Input required type="date" value={f.license_expiry} onChange={(e) => upd("license_expiry", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-license-expiry" /></div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Vehicle</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label className="text-xs uppercase text-slate-400">Make *</Label><Input required value={f.vehicle_make} onChange={(e) => upd("vehicle_make", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-make" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Model *</Label><Input required value={f.vehicle_model} onChange={(e) => upd("vehicle_model", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-model" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Year *</Label><Input required type="number" min="1990" max="2030" value={f.vehicle_year} onChange={(e) => upd("vehicle_year", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-year" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Plate *</Label><Input required value={f.vehicle_plate} onChange={(e) => upd("vehicle_plate", e.target.value.toUpperCase())} className="bg-black/30 border-white/10 h-11 mt-2 font-mono" data-testid="apply-plate" /></div>
              <div className="sm:col-span-2"><Label className="text-xs uppercase text-slate-400">Insurance expiry *</Label><Input required type="date" value={f.insurance_expiry} onChange={(e) => upd("insurance_expiry", e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" data-testid="apply-insurance-expiry" /></div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Documents (photos)</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <DocInput label="Driver's licence" required value={f.doc_license} onChange={(v) => upd("doc_license", v)} testid="apply-doc-license" />
              <DocInput label="Insurance certificate" required value={f.doc_insurance} onChange={(v) => upd("doc_insurance", v)} testid="apply-doc-insurance" />
              <DocInput label="Profile photo" required value={f.doc_profile} onChange={(v) => upd("doc_profile", v)} testid="apply-doc-profile" />
              <DocInput label="Vehicle photo" value={f.doc_vehicle} onChange={(v) => upd("doc_vehicle", v)} testid="apply-doc-vehicle" />
            </div>
          </div>

          <div className="border-t border-white/5 pt-5">
            <Label className="text-xs uppercase text-slate-400">Anything we should know? (optional)</Label>
            <Textarea value={f.notes} onChange={(e) => upd("notes", e.target.value)} className="bg-black/30 border-white/10 mt-2 min-h-[80px]" data-testid="apply-notes" placeholder="Prior driving experience, preferred hours, language, etc." />
          </div>

          <Button type="submit" disabled={loading} className="w-full btn-gold rounded-lg h-11" data-testid="apply-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit application
          </Button>
          <p className="text-[11px] text-slate-500 text-center">By submitting, you agree to a background & document check.</p>
        </form>
      </div>
    </div>
  );
}
